import React, { FunctionComponent, useRef } from 'react';
import { ApolloError } from 'apollo-boost';
import { match } from 'react-router-dom';
import { Grid, Header, Placeholder } from 'semantic-ui-react';
import withStyles from 'react-jss';
import { pathOr, isNil } from 'ramda';

import Query from 'components/Query';
import DownloadWidget from 'components/DownloadWidget';
import { useDatePicker } from 'components/DatePicker/DatePicker.context';
import { ClassProp } from 'helpers/types';
import { useMerchantOverview } from 'scenes/Home/scenes/MerchantStatusDetails/scenes/sections/MerchantOverview/MerchantOverview.context';
import { DemographicSegments } from 'components/Charts/Demographics/Demographics.helper';
import {
  DemographicsAgeChart,
  DemographicsGenderChart,
  DemographicsIncomeChart,
} from 'components/Charts';
import NoDataOverlay from 'components/NoDataOverlay';

import GET_DEMOGRAPHIC_SEGMENTS from './Demographics.queries';

type QueryResponse = {
  demographicSegments: DemographicSegments;
  loading?: boolean;
  error?: ApolloError;
};

type Styles = ClassProp<typeof styles>;

type Props = {
  classes: Styles;
  match: match;
};

const styles = {
  grid: {
    marginTop: '2rem !important',
    backgroundColor: '#fff',
  },
  container: {
    border: 'none !important',
    margin: '0 !important',
    padding: '0 !important',
    width: '100% !important',
  },
  noMargin: {
    margin: '0 !important',
  },
  formSelect: {
    marginLeft: '.75rem !important',
  },
  download: {
    background: '#fff',
  },
  contentRef: {
    padding: '0 !important',
  },
  placeholderContainer: {
    width: '100% !important',
    margin: '0 !important',
  },
  placeholderSize: {
    width: '21.875rem !important',
    height: '18.75rem !important',
  },
};

const renderHeader = (title: string, classes: Styles): JSX.Element => (
  <Header className={classes.noMargin} content={title} />
);

const renderPlaceHolder = (classes: Styles) => (
  <Grid className={classes.placeholderContainer} columns={3}>
    <Grid.Row>
      <Grid.Column>
        <Placeholder className={classes.placeholderSize}>
          <Placeholder.Image square />
        </Placeholder>
      </Grid.Column>
      <Grid.Column>
        <Placeholder className={classes.placeholderSize}>
          <Placeholder.Image square />
        </Placeholder>
      </Grid.Column>
      <Grid.Column>
        <Placeholder className={classes.placeholderSize}>
          <Placeholder.Image square />
        </Placeholder>
      </Grid.Column>
    </Grid.Row>
  </Grid>
);

const renderCharts = (demographicsData: DemographicSegments) => {
  const { age, income, gender } = demographicsData;
  const noData = isNil(age) && isNil(income) && isNil(gender);

  return (
    <Grid.Row>
      {noData && <NoDataOverlay />}
      <Grid.Column width={6}>
        <DemographicsAgeChart title="Age" ageGroups={age} />
      </Grid.Column>

      <Grid.Column width={4}>
        <DemographicsGenderChart
          innerRadius={50}
          outerRadius={90}
          title="Gender"
          genderGroup={gender}
        />
      </Grid.Column>

      <Grid.Column width={6}>
        <DemographicsIncomeChart
          title="Household Income"
          incomeGroups={income}
        />
      </Grid.Column>
    </Grid.Row>
  );
};

const renderDemographics = (
  classes: Styles,
  contentRef: React.MutableRefObject<null>,
) => (response: QueryResponse) => {
  const demographicsData = pathOr(
    [],
    ['data', 'demographicSegments'],
    response,
  ) as DemographicSegments;

  return (
    <Grid className={classes.grid}>
      <DownloadWidget
        nodeRef={contentRef}
        className={classes.download}
        header={renderHeader('Demographics', classes)}
        options={['PNG', 'PDF']}
        imageName={'Demographics'}
      />
      <div className={classes.contentRef} ref={contentRef}>
        <Grid className={classes.container}>
          {response.loading
            ? renderPlaceHolder(classes)
            : renderCharts(demographicsData)}
        </Grid>
      </div>
    </Grid>
  );
};

const Demographics: FunctionComponent<Props> = ({ classes, match }) => {
  const { userProfile } = useMerchantOverview();
  const { startDate, endDate } = useDatePicker();
  const contentRef = useRef(null);

  const merchantId = pathOr(
    userProfile.merchantId,
    ['params', 'merchantId'],
    match,
  );

  return (
    <Query
      enableProgressInfo
      query={GET_DEMOGRAPHIC_SEGMENTS}
      variables={{
        params: {
          startDate,
          endDate,
          merchantId,
        },
      }}
      render={renderDemographics(classes, contentRef)}
    />
  );
};

export default withStyles(styles)(Demographics);
